import { useEffect, useMemo, useState } from "react";
import {
  pollUntilDone,
  submit,
  type ManifestEntry,
  type StatusResponse,
} from "./api";
import { DropZone } from "./components/DropZone";

type Phase =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "running"; callId: string; startedAt: number }
  | { kind: "done"; manifest: ManifestEntry[]; renderSeconds?: number }
  | { kind: "failed"; error: string };

export function App() {
  const [audio, setAudio] = useState<File | null>(null);
  const [cover, setCover] = useState<File | null>(null);
  const [metaFile, setMetaFile] = useState<File | null>(null);
  const [metaText, setMetaText] = useState<string>("");
  const [visualImages, setVisualImages] = useState<File[]>([]);
  const [preset, setPreset] = useState<string>("");
  const [fullPipeline, setFullPipeline] = useState<boolean>(true);
  const [only, setOnly] = useState<"" | "full" | "shorts" | "canvas">("");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [tick, setTick] = useState(0); // forces re-render for the timer

  // Force re-render every second while running so the elapsed timer ticks.
  useEffect(() => {
    if (phase.kind !== "running") return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [phase.kind]);

  // Read metadata file into metaText when picked, so we can show a preview
  // and edit-in-place isn't surprising. Also extract visualImages list
  // from the meta so the operator can see what we expect.
  useEffect(() => {
    if (!metaFile) {
      setMetaText("");
      return;
    }
    metaFile.text().then(setMetaText);
  }, [metaFile]);

  const metaVisualImageNames: string[] = useMemo(() => {
    if (!metaText) return [];
    try {
      const j = JSON.parse(metaText);
      if (Array.isArray(j.visualImages)) {
        return j.visualImages.map((p: string) => p);
      }
    } catch {
      // ignore parse errors -- preview will surface them visually below.
    }
    return [];
  }, [metaText]);

  const canSubmit = audio !== null && cover !== null && metaText.length > 0
    && phase.kind === "idle";

  async function onSubmit() {
    if (!audio || !cover || !metaText) return;
    setPhase({ kind: "submitting" });

    try {
      // Build the visualImages list. We need names (meta-relative) matched
      // to File objects from the picker. If the meta declares N images,
      // we look for files whose basenames match (last segment of the path).
      const visualEntries = metaVisualImageNames
        .map((name) => {
          const base = name.split("/").pop()!;
          const file = visualImages.find((f) => f.name === base);
          if (!file) return null;
          return { name, file };
        })
        .filter((x): x is { name: string; file: File } => x !== null);

      const resp = await submit({
        audio,
        cover,
        meta: metaText,
        visualImages: visualEntries,
        preset: preset || undefined,
        only: only || undefined,
        fullPipeline,
        concurrency: 16,
      });

      setPhase({
        kind: "running",
        callId: resp.call_id,
        startedAt: Date.now(),
      });

      const final = await pollUntilDone(resp.call_id, { intervalMs: 4000 });
      handleTerminal(final);
    } catch (err) {
      setPhase({ kind: "failed", error: (err as Error).message });
    }
  }

  function handleTerminal(status: StatusResponse) {
    switch (status.state) {
      case "done":
        setPhase({
          kind: "done",
          manifest: status.manifest,
          renderSeconds: status.render_seconds,
        });
        return;
      case "failed":
        setPhase({ kind: "failed", error: status.error });
        return;
      case "expired":
        setPhase({
          kind: "failed",
          error: "Render output expired before download.",
        });
        return;
      case "running":
        // Shouldn't happen -- pollUntilDone only returns terminal states.
        return;
    }
  }

  function reset() {
    setPhase({ kind: "idle" });
  }

  // Elapsed timer for the running phase. tick (see effect above) forces
  // a re-render each second so this renders in real time.
  void tick;
  const elapsedSeconds =
    phase.kind === "running"
      ? Math.floor((Date.now() - phase.startedAt) / 1000)
      : null;

  return (
    <div className="min-h-screen px-6 py-10 sm:py-16">
      <div className="mx-auto max-w-3xl">
        <header className="mb-10 flex items-baseline justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              Burnish Visualizer
            </h1>
            <p className="mt-1 text-sm text-zinc-400">
              Cloud render: full YouTube + shorts + Spotify Canvas + release
              captions. ~10 min, ~$0.20 per track.
            </p>
          </div>
          <a
            href="https://modal.com/apps/bcrumrin/main/deployed/burnish-visualizer"
            target="_blank"
            rel="noreferrer"
            className="text-xs text-zinc-500 hover:text-zinc-300"
          >
            Modal dashboard ↗
          </a>
        </header>

        {phase.kind === "idle" || phase.kind === "submitting" ? (
          <SubmitForm
            audio={audio}
            setAudio={setAudio}
            cover={cover}
            setCover={setCover}
            metaFile={metaFile}
            setMetaFile={setMetaFile}
            metaText={metaText}
            visualImages={visualImages}
            setVisualImages={setVisualImages}
            metaVisualImageNames={metaVisualImageNames}
            preset={preset}
            setPreset={setPreset}
            fullPipeline={fullPipeline}
            setFullPipeline={setFullPipeline}
            only={only}
            setOnly={setOnly}
            canSubmit={canSubmit}
            submitting={phase.kind === "submitting"}
            onSubmit={onSubmit}
          />
        ) : null}

        {phase.kind === "running" ? (
          <RunningPanel
            elapsedSeconds={elapsedSeconds!}
            callId={phase.callId}
            fullPipeline={fullPipeline}
          />
        ) : null}

        {phase.kind === "done" ? (
          <DonePanel
            manifest={phase.manifest}
            renderSeconds={phase.renderSeconds}
            onReset={reset}
          />
        ) : null}

        {phase.kind === "failed" ? (
          <FailedPanel error={phase.error} onReset={reset} />
        ) : null}
      </div>
    </div>
  );
}

interface SubmitFormProps {
  audio: File | null;
  setAudio: (f: File | null) => void;
  cover: File | null;
  setCover: (f: File | null) => void;
  metaFile: File | null;
  setMetaFile: (f: File | null) => void;
  metaText: string;
  visualImages: File[];
  setVisualImages: (fs: File[]) => void;
  metaVisualImageNames: string[];
  preset: string;
  setPreset: (s: string) => void;
  fullPipeline: boolean;
  setFullPipeline: (b: boolean) => void;
  only: "" | "full" | "shorts" | "canvas";
  setOnly: (o: "" | "full" | "shorts" | "canvas") => void;
  canSubmit: boolean;
  submitting: boolean;
  onSubmit: () => void;
}

function SubmitForm(props: SubmitFormProps) {
  const {
    audio,
    setAudio,
    cover,
    setCover,
    metaFile,
    setMetaFile,
    metaText,
    visualImages,
    setVisualImages,
    metaVisualImageNames,
    preset,
    setPreset,
    fullPipeline,
    setFullPipeline,
    only,
    setOnly,
    canSubmit,
    submitting,
    onSubmit,
  } = props;

  const missingVisualImages = useMemo(() => {
    if (metaVisualImageNames.length === 0) return [];
    return metaVisualImageNames.filter((path) => {
      const base = path.split("/").pop()!;
      return !visualImages.some((f) => f.name === base);
    });
  }, [metaVisualImageNames, visualImages]);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <DropZone
          label="Audio"
          accept="audio/*,.wav,.mp3,.flac"
          file={audio}
          onPick={setAudio}
          hint="WAV / MP3 / FLAC"
        />
        <DropZone
          label="Cover art"
          accept="image/*,.png,.jpg,.jpeg"
          file={cover}
          onPick={setCover}
          hint="PNG / JPG"
        />
      </div>

      <DropZone
        label="Metadata JSON"
        accept="application/json,.json"
        file={metaFile}
        onPick={setMetaFile}
        hint="SongMetadata: artist, title, genre, mood, lyrics, visualImages…"
      />

      {metaText && (
        <details className="panel px-4 py-3 text-sm">
          <summary className="cursor-pointer text-zinc-300">
            Metadata preview ({metaText.length} chars)
          </summary>
          <pre className="mt-3 max-h-64 overflow-auto text-xs text-zinc-400">
            {metaText}
          </pre>
        </details>
      )}

      {metaVisualImageNames.length > 0 && (
        <div className="panel px-4 py-3 text-sm">
          <div className="mb-2 flex items-baseline justify-between">
            <div className="font-medium text-zinc-200">
              Visual images ({metaVisualImageNames.length} expected)
            </div>
            {missingVisualImages.length === 0 && visualImages.length > 0 ? (
              <span className="text-xs text-emerald-400">all matched</span>
            ) : null}
          </div>
          <ul className="space-y-1 text-xs text-zinc-400">
            {metaVisualImageNames.map((path) => {
              const base = path.split("/").pop()!;
              const matched = visualImages.find((f) => f.name === base);
              return (
                <li key={path} className="flex items-center gap-2">
                  <span
                    className={
                      matched
                        ? "h-1.5 w-1.5 rounded-full bg-emerald-400"
                        : "h-1.5 w-1.5 rounded-full bg-zinc-600"
                    }
                  />
                  <code className="font-mono">{path}</code>
                  {matched && (
                    <span className="text-zinc-500">
                      ({(matched.size / 1024).toFixed(0)} KB)
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <label className="mt-3 inline-block cursor-pointer text-xs text-accent-400 hover:text-accent-300">
            <input
              type="file"
              accept="image/*,.png,.jpg,.jpeg"
              multiple
              className="hidden"
              onChange={(e) =>
                setVisualImages(Array.from(e.target.files ?? []))
              }
            />
            Pick all visualImages files…
          </label>
        </div>
      )}

      <div className="panel space-y-4 px-4 py-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs text-zinc-400">Preset (optional)</span>
            <select
              className="mt-1 block w-full rounded-md border border-ink-600 bg-ink-800 px-3 py-2 text-sm"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
            >
              <option value="">auto (from genre)</option>
              <option value="trip-hop-dust">trip-hop-dust</option>
              <option value="edm-404-drop">edm-404-drop</option>
              <option value="hip-hop-night-drive">hip-hop-night-drive</option>
              <option value="jawaiian-sunset">jawaiian-sunset</option>
              <option value="dark-trap-system">dark-trap-system</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-zinc-400">Render</span>
            <select
              className="mt-1 block w-full rounded-md border border-ink-600 bg-ink-800 px-3 py-2 text-sm"
              value={only}
              onChange={(e) =>
                setOnly(e.target.value as "" | "full" | "shorts" | "canvas")
              }
            >
              <option value="">everything</option>
              <option value="full">full only (16:9)</option>
              <option value="shorts">shorts only (3x 9:16)</option>
              <option value="canvas">canvas only (Spotify 9:16 6s)</option>
            </select>
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-500 bg-ink-800 text-accent-500"
            checked={fullPipeline}
            onChange={(e) => setFullPipeline(e.target.checked)}
          />
          <span className="text-zinc-300">
            Also generate release content (captions, hashtags, posting schedule)
          </span>
        </label>
      </div>

      <button
        type="button"
        className="btn-primary w-full"
        disabled={!canSubmit}
        onClick={onSubmit}
      >
        {submitting ? "Uploading…" : "Generate"}
      </button>
    </div>
  );
}

function RunningPanel({
  elapsedSeconds,
  callId,
  fullPipeline,
}: {
  elapsedSeconds: number;
  callId: string;
  fullPipeline: boolean;
}) {
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  // Rough ETA: full pipeline ~10 min, only-renders ~6 min, shorts-only ~3 min.
  // We don't know "only" mode here; show a generic upper bound.
  const eta = fullPipeline ? "≈ 10 min" : "≈ 6 min";
  return (
    <div className="panel space-y-3 px-6 py-8">
      <div className="flex items-center gap-3">
        <div className="h-3 w-3 animate-pulse rounded-full bg-accent-400" />
        <h2 className="text-lg font-medium text-zinc-100">Rendering on Modal</h2>
      </div>
      <p className="text-sm text-zinc-400">
        Elapsed: {minutes}m {seconds.toString().padStart(2, "0")}s ({eta} total)
      </p>
      <p className="font-mono text-xs text-zinc-500">call: {callId}</p>
      <p className="text-xs text-zinc-500">
        Status is polled every 4 seconds. Feel free to keep this tab in the
        background; it will update when the render lands.
      </p>
    </div>
  );
}

function DonePanel({
  manifest,
  renderSeconds,
  onReset,
}: {
  manifest: ManifestEntry[];
  renderSeconds?: number;
  onReset: () => void;
}) {
  const grouped = useMemo(() => groupByDir(manifest), [manifest]);
  const totalBytes = manifest.reduce((s, e) => s + e.size_bytes, 0);
  return (
    <div className="panel space-y-5 px-6 py-7">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-medium text-zinc-100">
          Render complete{" "}
          <span aria-hidden className="text-accent-400">
            ●
          </span>
        </h2>
        <button type="button" className="btn-ghost" onClick={onReset}>
          New render
        </button>
      </div>
      <p className="text-xs text-zinc-500">
        {manifest.length} files · {formatBytes(totalBytes)}
        {renderSeconds !== undefined
          ? ` · ${Math.round(renderSeconds)}s on Modal`
          : null}
      </p>
      {Object.entries(grouped).map(([dir, entries]) => (
        <section key={dir}>
          <h3 className="mb-2 text-xs uppercase tracking-wide text-zinc-500">
            {dir || "root"}
          </h3>
          <ul className="space-y-1.5">
            {entries.map((e) => (
              <li
                key={e.rel_path}
                className="flex items-baseline justify-between gap-3"
              >
                <a
                  href={e.url}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate font-mono text-sm text-accent-300 hover:text-accent-200"
                >
                  {basename(e.rel_path)}
                </a>
                <span className="shrink-0 text-xs text-zinc-500">
                  {formatBytes(e.size_bytes)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function FailedPanel({
  error,
  onReset,
}: {
  error: string;
  onReset: () => void;
}) {
  return (
    <div className="panel space-y-3 px-6 py-7 border-red-900/50">
      <h2 className="text-lg font-medium text-red-300">Render failed</h2>
      <pre className="max-h-64 overflow-auto rounded bg-ink-950 p-3 text-xs text-zinc-400">
        {error}
      </pre>
      <button type="button" className="btn-ghost" onClick={onReset}>
        Reset
      </button>
    </div>
  );
}

function groupByDir(entries: ManifestEntry[]): Record<string, ManifestEntry[]> {
  const out: Record<string, ManifestEntry[]> = {};
  for (const e of entries) {
    const slash = e.rel_path.lastIndexOf("/");
    const dir = slash === -1 ? "" : e.rel_path.slice(0, slash);
    (out[dir] ??= []).push(e);
  }
  return out;
}

function basename(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash === -1 ? path : path.slice(slash + 1);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
